import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import WaveformViz from '@/components/WaveformViz';
import Logo from '@/components/Logo';

/* ------------------------------------------------------------------ */
/*  Ícones (line-style, herdam a cor via currentColor)                 */
/* ------------------------------------------------------------------ */

const svg = {
  width: 26,
  height: 26,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

/** Biometria comportamental — impressão digital */
const FingerprintIcon = () => (
  <svg {...svg}>
    <path d="M12 10a2 2 0 0 0-2 2c0 1.02-.1 2.51-.26 4" />
    <path d="M14 13.12c0 2.38 0 6.38-1 8.88" />
    <path d="M17.29 21.02c.12-.6.43-2.3.5-3.02" />
    <path d="M2 12a10 10 0 0 1 18-6" />
    <path d="M2 16h.01" />
    <path d="M21.8 16c.2-2 .13-5.35 0-6" />
    <path d="M5 19.5C5.5 18 6 15 6 12a6 6 0 0 1 .34-2" />
    <path d="M8.65 22c.21-.66.45-1.32.57-2" />
    <path d="M9 6.8a6 6 0 0 1 9 5.2v2" />
  </svg>
);

/** Sem senhas — cadeado aberto */
const UnlockIcon = () => (
  <svg {...svg}>
    <rect width="18" height="11" x="3" y="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 9.9-1" />
    <circle cx="12" cy="16" r="1" />
  </svg>
);

/** Segurança contínua — escudo com pulso */
const ShieldPulseIcon = () => (
  <svg {...svg}>
    <path d="M20 13c0 5-3.5 7.4-7.66 8.95a1 1 0 0 1-.67 0C7.5 20.4 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.8 17 5 19 5a1 1 0 0 1 1 1z" />
    <path d="M8 12.5h2L11.3 10l1.7 4 1.2-2h1.8" />
  </svg>
);

/** Privacidade por design — dispositivo com cadeado (dados locais) */
const DeviceLockIcon = () => (
  <svg {...svg}>
    <rect x="2.5" y="4" width="19" height="12.5" rx="2" />
    <path d="M8.5 20.5h7M12 16.5v4" />
    <rect x="10.1" y="9" width="3.8" height="3.4" rx="0.7" />
    <path d="M10.9 9v-.9a1.1 1.1 0 0 1 2.2 0V9" />
  </svg>
);

/** Check para os selos de confiança */
const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

/** Passo 1 — teclado */
const KeyboardIcon = () => (
  <svg {...svg}>
    <rect x="2" y="6" width="20" height="12" rx="2" />
    <path d="M6 10h.01M10 10h.01M14 10h.01M18 10h.01M7 14h10" />
  </svg>
);

/** Passo 2 — análise do ritmo (waveform) */
const WaveIcon = () => (
  <svg {...svg}>
    <path d="M2 12h3l2.5-7 3.5 14 3-10 2 5h6" />
  </svg>
);

/* ------------------------------------------------------------------ */

const FEATURES = [
  {
    Icon: FingerprintIcon,
    color: '#7c5cfc',
    title: 'Biometria comportamental',
    desc: 'Seu ritmo de digitação é único — impossível de copiar, esquecer ou roubar.',
  },
  {
    Icon: UnlockIcon,
    color: '#4fc3f7',
    title: 'Sem senhas',
    desc: 'Nada para memorizar nem redefinir. Você digita e já está autenticado.',
  },
  {
    Icon: ShieldPulseIcon,
    color: '#00d26a',
    title: 'Segurança contínua',
    desc: 'Verificação em tempo real durante toda a sessão — não só no login.',
  },
  {
    Icon: DeviceLockIcon,
    color: '#f5a623',
    title: 'Privacidade por design',
    desc: 'Seu padrão biométrico permanece no seu dispositivo. Zero dados sensíveis expostos.',
  },
];

const TRUST = ['Conforme a LGPD', 'Sem senhas para vazar', 'Verificação em tempo real', 'Modelo no dispositivo'];

const STEPS = [
  {
    Icon: KeyboardIcon,
    title: 'Digite uma frase',
    desc: 'Você digita um texto curto algumas vezes durante o cadastro. Só isso.',
  },
  {
    Icon: WaveIcon,
    title: 'Criamos sua assinatura',
    desc: 'O modelo aprende o ritmo, a cadência e as pausas únicas da sua digitação.',
  },
  {
    Icon: UnlockIcon,
    title: 'Entre só digitando',
    desc: 'Na próxima vez, é só digitar. Sem senha, sem código, sem fricção.',
  },
];

const FAQ = [
  {
    q: 'Preciso de senha para entrar?',
    a: 'Não. A sua autenticação é o próprio ritmo da sua digitação — a forma única como você pressiona e solta as teclas. Não há senha para criar, lembrar, vazar ou redefinir.',
  },
  {
    q: 'Como isso é mais seguro do que uma senha?',
    a: 'Senhas podem ser roubadas, capturadas por phishing ou reaproveitadas em vários serviços. O seu padrão de digitação é comportamental e involuntário: mesmo que alguém veja você digitar, não consegue reproduzir a sua cadência, as micro-pausas e a coordenação entre as mãos.',
  },
  {
    q: 'E se tentarem imitar ou automatizar a minha digitação?',
    a: 'O sistema tem detecção anti-fraude que identifica texto colado, digitação sintética e padrões robóticos. Além disso, a verificação é contínua durante toda a sessão — não apenas no momento do login.',
  },
  {
    q: 'E se eu digitar diferente um dia (cansaço, lesão, teclado novo)?',
    a: 'O HKS trabalha com uma zona de decisão: em caso de dúvida, ele solicita uma verificação adicional (step-up) em vez de bloquear ou liberar às cegas. O seu padrão também é atualizado ao longo do tempo para acompanhar mudanças naturais.',
  },
  {
    q: 'Onde ficam os meus dados? Vocês guardam o que eu escrevo?',
    a: 'Não guardamos o conteúdo do que você digita — apenas métricas de tempo (durações e intervalos entre as teclas). O seu padrão é armazenado como um vetor matemático, não como texto legível, e não é reversível para reconstruir o que foi digitado.',
  },
  {
    q: 'Isto está de acordo com a LGPD?',
    a: 'Sim. O tratamento segue os princípios da LGPD — finalidade, minimização e segurança. Os dados biométricos são tratados com base legal adequada e você pode solicitar acesso, correção ou exclusão a qualquer momento.',
  },
  {
    q: 'E padrões como o ISO/IEC 27001?',
    a: 'A plataforma é construída seguindo boas práticas de segurança da informação alinhadas ao ISO/IEC 27001: controle de acesso, criptografia em trânsito, registro de auditoria e privacy by design desde a concepção.',
  },
];

/**
 * Landing page — hero com waveform animado, features e prova de valor.
 */
export default function Landing() {
  const [waveData, setWaveData] = useState<number[]>([]);
  const intervalRef = useRef<ReturnType<typeof setInterval>>();

  useEffect(() => {
    const initial = Array.from({ length: 30 }, () => 80 + Math.random() * 120);
    setWaveData(initial);
    intervalRef.current = setInterval(() => {
      setWaveData((prev) => [...prev.slice(1), 60 + Math.random() * 150]);
    }, 200);
    return () => clearInterval(intervalRef.current);
  }, []);

  const features = useMemo(() => FEATURES, []);

  return (
    <div className="relative min-h-screen flex flex-col overflow-x-hidden">
      {/* glow ambiente de fundo */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            'radial-gradient(60% 45% at 50% 0%, rgba(124,92,252,0.14), transparent 70%), radial-gradient(40% 30% at 85% 20%, rgba(79,195,247,0.10), transparent 70%)',
        }}
      />

      {/* Nav */}
      <nav className="flex items-center justify-between px-6 py-4 border-b border-bg-surface/50">
        <Logo markSize="h-9" wordClass="text-lg" />
        <div className="flex items-center gap-4">
          <Link
            to="/login"
            className="text-sm text-txt-muted hover:text-txt-primary transition-colors font-sans"
          >
            Entrar
          </Link>
          <Link
            to="/onboarding"
            className="text-sm bg-accent-brand hover:bg-accent-brand/90 text-white px-4 py-2 rounded-lg font-sans font-medium transition-colors"
          >
            Começar grátis
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 pt-14">
        <div className="max-w-3xl text-center mb-12 animate-fade-in">
          {/* eyebrow */}
          <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full border border-accent-brand/25 bg-accent-brand/5">
            <span className="w-1.5 h-1.5 rounded-full bg-accent-success animate-pulse-dot" />
            <span className="text-xs font-sans font-medium text-txt-muted tracking-wide">
              Autenticação biométrica comportamental
            </span>
          </div>

          <h1 className="text-4xl md:text-6xl font-bold text-txt-primary font-sans leading-[1.05] mb-6">
            Sua digitação é sua{' '}
            <span
              className="text-transparent bg-clip-text"
              style={{ backgroundImage: 'linear-gradient(120deg, #7c5cfc, #4fc3f7)' }}
            >
              assinatura
            </span>
          </h1>
          <p className="text-lg md:text-xl text-txt-muted font-sans max-w-xl mx-auto mb-8 leading-relaxed">
            O ENI HKS transforma o ritmo único da sua digitação em uma chave biométrica.
            Sem senhas, sem tokens, sem fricção — apenas você.
          </p>

          {/* Waveform hero */}
          <div className="mb-9 flex justify-center">
            <WaveformViz
              data={waveData}
              width={600}
              height={100}
              color="#7c5cfc"
              maxPoints={30}
              animate={true}
              showGrid={false}
            />
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/onboarding"
              className="inline-flex items-center gap-2 bg-accent-brand hover:bg-accent-brand/90 text-white px-8 py-3.5 rounded-xl font-sans font-semibold text-base transition-all hover:shadow-lg hover:shadow-accent-brand/25 hover:-translate-y-0.5"
            >
              Criar minha assinatura
              <span aria-hidden>&rarr;</span>
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 border border-bg-surface hover:border-accent-brand/50 text-txt-primary px-8 py-3.5 rounded-xl font-sans font-medium text-base transition-all"
            >
              Já tenho conta
            </Link>
          </div>
        </div>

        {/* Features grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 max-w-5xl w-full mb-10 animate-slide-up">
          {features.map(({ Icon, color, title, desc }, i) => (
            <div
              key={i}
              className="group relative p-6 bg-bg-secondary/70 border border-bg-surface rounded-card transition-all duration-300 hover:-translate-y-1"
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = `${color}66`;
                e.currentTarget.style.boxShadow = `0 16px 40px -12px ${color}40`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '';
                e.currentTarget.style.boxShadow = '';
              }}
            >
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-5 transition-transform duration-300 group-hover:scale-110"
                style={{
                  color,
                  background: `${color}1a`,
                  boxShadow: `inset 0 0 0 1px ${color}33`,
                }}
              >
                <Icon />
              </div>
              <h3 className="text-base font-semibold text-txt-primary font-sans mb-2">
                {title}
              </h3>
              <p className="text-sm text-txt-muted font-sans leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>

        {/* Como funciona */}
        <section className="w-full max-w-5xl mb-16">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-txt-primary font-sans mb-2">Como funciona</h2>
            <p className="text-sm text-txt-muted font-sans">
              Três passos, menos de dois minutos — e nunca mais uma senha.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {STEPS.map(({ Icon, title, desc }, i) => (
              <div key={i} className="relative p-6 rounded-card bg-bg-secondary/40 text-center">
                <span
                  className="absolute top-4 right-5 font-mono text-4xl font-bold leading-none select-none"
                  style={{ color: 'rgba(124,92,252,0.18)' }}
                >
                  0{i + 1}
                </span>
                <div
                  className="mx-auto mb-5 w-14 h-14 rounded-2xl flex items-center justify-center text-accent-brand bg-accent-brand/10"
                  style={{ boxShadow: 'inset 0 0 0 1px rgba(124,92,252,0.2)' }}
                >
                  <Icon />
                </div>
                <h3 className="text-base font-semibold text-txt-primary font-sans mb-2">{title}</h3>
                <p className="text-sm text-txt-muted font-sans leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Trust badges */}
        <div className="flex flex-wrap items-center justify-center gap-x-7 gap-y-2.5 max-w-4xl mb-16">
          {TRUST.map((t) => (
            <span key={t} className="inline-flex items-center gap-1.5 text-xs font-sans text-txt-muted">
              <span className="text-accent-success">
                <CheckIcon />
              </span>
              {t}
            </span>
          ))}
        </div>

        {/* FAQ */}
        <section className="w-full max-w-3xl mb-16">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-txt-primary font-sans mb-2">
              Perguntas frequentes
            </h2>
            <p className="text-sm text-txt-muted font-sans">
              Segurança, privacidade e conformidade — sem letras miúdas.
            </p>
          </div>
          <div className="space-y-3">
            {FAQ.map(({ q, a }, i) => (
              <details
                key={i}
                className="group rounded-card border border-bg-surface bg-bg-secondary/50 px-5 py-4 transition-colors hover:border-accent-brand/30 [&_summary]:list-none"
              >
                <summary className="flex items-center justify-between gap-4 cursor-pointer">
                  <span className="text-sm md:text-base font-medium text-txt-primary font-sans">{q}</span>
                  <span className="shrink-0 text-2xl leading-none text-accent-brand transition-transform duration-200 group-open:rotate-45">
                    +
                  </span>
                </summary>
                <p className="mt-3 text-sm text-txt-muted font-sans leading-relaxed">{a}</p>
              </details>
            ))}
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="flex flex-col items-center gap-4 py-9 border-t border-bg-surface/50">
        <img
          src="/brand/hks-logo-light.png"
          alt="ENI HKS — Human Keystroke Signature — Behavioral Biometric Authentication"
          className="h-14 w-auto"
        />
        <p className="text-xs text-txt-muted font-sans">
          © {new Date().getFullYear()} Ethereal Nexus Institute &middot; Todos os direitos reservados
        </p>
      </footer>
    </div>
  );
}
